import { LDMultiKindContext, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type FeatureFlagUserDetail = {
  login?: string;
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  roleId?: number;
  roleTypeId?: number;
  firmId?: string;
};

export function useFeatureFlagContext() {
  const ldClient = useLDClient();
  const [context, setContext] = useState<LDMultiKindContext>();
  const prevContextRef = useRef<LDMultiKindContext | undefined>(undefined);
  const isContextSet = context !== undefined;
  const flags = useFlags();
  const isFlagsLoaded = useMemo(() => Object.keys(flags).length > 0, [flags]);

  useEffect(() => {
    const prevContext = prevContextRef.current;

    const identifyUser = () => {
      if (context) {
        ldClient?.identify(context).catch((error) => {
          console.error('Failed to identify user in LaunchDarkly:', error);
        });
      } else if (!context && prevContext) {
        console.error('Context is missing or invalid. Falling back to default context.');
        ldClient?.identify({ key: 'anonymous-user' }).catch((error) => {
          console.error('Failed to identify anonymous user in LaunchDarkly:', error);
        });
      }
    };

    prevContextRef.current = context;
    identifyUser();
  }, [context, ldClient]);

  const clearUserContext = useCallback(() => {
    setContext(undefined);
  }, []);

  const setUserContext = useCallback((userDetail: FeatureFlagUserDetail) => {
    let fullName = userDetail.firstName ? userDetail.firstName + ' ' : '';
    fullName += userDetail.lastName ? userDetail.lastName : '';

    const context: LDMultiKindContext = {
      kind: 'multi',
      user: {
        key: userDetail.login,
        email: userDetail.emailAddress,
        name: fullName,
      },

      custom: {
        key: userDetail.login,
        roleId: userDetail.roleId,
        roleTypeId: userDetail.roleTypeId,
        firmId: userDetail.firmId,
      },
    };

    setContext((ctx) => {
      if (isEqual(ctx, context)) {
        return ctx;
      }
      return context;
    });
  }, []);

  return { setUserContext, clearUserContext, isContextSet, isFlagsLoaded };
}
